// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { JwtHelperService } from '@auth0/angular-jwt';
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Service imports
import { AuthService } from '../auth.service';
import { MPCacheRegistrationService } from '../shared-services/mp-cache-registration-service';
import { QaUserService } from './qa-user.service';

// Models imports
import { ResultStatusDto } from '../../_models/result-status-dto';
import { UserInfoResultDto } from '../../_models/user-info-result-dto';
import { ResetPasswordParametersDto } from '../../_models/reset-password-parameters-dto';
import { BrandingConfigurationResultDto } from '../../_models/branding-configuration-result-dto';
import { ActionAccessListParameterDto } from '../../_models/action-access-list-parameter-dto';
import { ActionAccessResultDto } from '../../_models/action-access-result-dto';
import { UserSettingParmetersDto } from '../../_models/common/user-setting-parmeters-dto';
import { UserSettingRawDto } from '../../_models/common/user-setting-raw-dto';
import { ForgotPasswordTokenParametersDto } from '../../_models/forgot-password-token-parameters-dto';
import { CloudDriveUrlResultDto } from '../../_models/cloud-drive-url-result-dto';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { Constants } from '../../_constants/constants';
import { SideNavigationConstants } from '../../_constants/navigation-constants';
import { SideNavigationTree } from '../../_constants/side-navigation-tree';
import { ConfigNavigationConstants } from '../../_constants/config-navigation-constants';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly _subjectSideNavigatorState: Subject<boolean> = new Subject();
    private readonly _subjectTopMenuState: Subject<boolean> = new Subject();
    private readonly _subjectChangesInPrefences: Subject<boolean> = new Subject();
    private readonly _subjectChangeInThemeColor: Subject<boolean> = new Subject();

    private _brandingInforamtion: BrandingConfigurationResultDto;

    jwtHelper = new JwtHelperService();
    decodedToken: any;
    subjectNoActionItem: Subject<boolean> = new Subject();
    userInfo: UserInfoResultDto = null;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly authService: AuthService,
        private readonly qaUserService: QaUserService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {
        this.authService.getSubjectLogout().subscribe((flag: boolean) => {
            if (flag === true) {
                this.localStorageService.clear().subscribe(() => {
                    this.userInfo = null;
                    this.localStorageService.userId = null;
                });
            }
        });
        this.loadUserSettings().subscribe();
        this.qaUserService.loadQaUsers().subscribe();
    }

    forgotPassword(forgotPasswordParametersDto: ForgotPasswordTokenParametersDto): Observable<any> {
        return this.httpClient.post(
            URLConstants.ccUserBaseApiUrl + '/forgotpassword',
            classToPlain(forgotPasswordParametersDto)
        );
    }

    resetPassword(resetPasswordParametersDto: ResetPasswordParametersDto): Observable<ResultStatusDto> {
        return this.httpClient
            .put<ResultStatusDto>(
                URLConstants.ccUserBaseApiUrl + '/resetpassword',
                classToPlain(resetPasswordParametersDto)
            )
            .pipe(map((responseData) => plainToClass(ResultStatusDto, responseData)));
    }

    getCurrentUserId(): string {
        return this.getUserIDByTokenName(StorageKeyConstants.apiTokenName);
    }

    getUserIDByTokenName(tokenName: string) {
        const token = localStorage.getItem(tokenName);
        if (!!token) {
            const decoded = this.jwtHelper.decodeToken(token);
            if (!!decoded.unique_name) {
                return decoded.unique_name;
            } else if (!!decoded.nameid) {
                return decoded.nameid;
            }
        }
        return null;
    }

    getCurrentUserInfo(): UserInfoResultDto {
        if (this.userInfo === null && this.authService.isLoggedIn === true) {
            const userInfoJson = localStorage.getItem(StorageKeyConstants.userInfo);
            if (!!userInfoJson) {
                this.userInfo = JSON.parse(userInfoJson);
            }
        }
        return plainToClass(UserInfoResultDto, this.userInfo);
    }

    setCurrentUserInfo(userInfo: UserInfoResultDto): void {
        this.userInfo = userInfo;
        localStorage.setItem(StorageKeyConstants.userInfo, JSON.stringify(userInfo));
    }

    isITUser(): boolean {
        return this.isInRole('IT');
    }

    isInternal(): boolean {
        return (
            Utils.notNullAndDefined(this.userInfo) &&
            Utils.notNullAndDefined(this.userInfo.isInternal) &&
            Utils.isTrue(this.userInfo.isInternal)
        );
    }

    isShadowLogin(): boolean {
        const shadowLogin = localStorage.getItem(StorageKeyConstants.apiIsShadowLogin);
        if (Utils.notNullAndDefined(shadowLogin)) {
            return JSON.parse(shadowLogin);
        } else {
            return false;
        }
    }

    isBoardMember(): boolean {
        return !!this.userInfo && this.userInfo.isInternal === false && this.isShadowLogin() === false;
    }

    isInRole(role: string): boolean {
        const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
        const decoded = this.jwtHelper.decodeToken(token);
        if (!!decoded && !!decoded.role) {
            const userRoles = decoded.role.toUpperCase().split(',');
            if (userRoles.length > 0 && userRoles.indexOf(role) >= 0) {
                return true;
            }
        }
        return false;
    }

    isContextReady(selectedContext: any): boolean {
        const hasContextUrl =
            new RegExp('/community/[0-9]+/').test(location.href) ||
            new RegExp('/property/[0-9]+/').test(location.href) ||
            new RegExp('/cb/[0-9]+/').test(location.href) ||
            new RegExp('/tools/[0-9]+/').test(location.href);
        return !hasContextUrl || (hasContextUrl && !!selectedContext);
    }

    isInDevOrQaRelease(menu: ActionAccessResultDto): boolean {
        return Utils.isInList(
            menu.code.toUpperCase(),
            SideNavigationConstants.QA_ACTION_ACCESS.concat(SideNavigationConstants.IT_ACTION_ACCESS)
        );
    }

    userPortalInfo(clearCache = false): Observable<UserInfoResultDto> {
        const url = `${URLConstants.ccUserPortalInfoApiUrl}?portalType=${Constants.portalType}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get<UserInfoResultDto>(url, options)
            .pipe(map((response) => plainToClass(UserInfoResultDto, response)));
    }

    getActionList(baseParams: ActionAccessListParameterDto): Observable<ActionAccessResultDto[]> {
        const url = `${URLConstants.ccUserBaseApiUrl}/availableactions${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient.get<ActionAccessResultDto[]>(url, options).pipe(
            map((response: any[]) => {
                const plainItems = Utils.uniqueList(
                    [...response, ...SideNavigationConstants.TEMP_ACTION_ACCESS],
                    'code'
                );
                const filteredMenuItems = plainItems.filter((menu) => {
                    let isValid = (menu.internalOnly && this.isInternal()) || !menu.internalOnly;
                    const isQaMenu = this.isInDevOrQaRelease(menu);
                    if (isValid && isQaMenu) {
                        isValid =
                            this.isITUser() ||
                            this.qaUserService.hasQaReleaseAccess(menu.code, this.getCurrentUserId());
                    }
                    return isValid;
                });
                return plainToClass(ActionAccessResultDto, filteredMenuItems);
            })
        );
    }

    getQaActionList(): Observable<ActionAccessResultDto[]> {
        const plainItems = SideNavigationTree.flattenSideNavTreeItems(SideNavigationTree.masterTreeItems);
        let filteredActions: any[] = [];
        plainItems.forEach((menu) => {
            if (Utils.isInList(menu.name.toUpperCase(), SideNavigationConstants.QA_ACTION_ACCESS)) {
                filteredActions.push({
                    code: menu.name,
                    title: menu.text,
                    description: menu.text,
                    internalOnly: false,
                    specificRoleOnly: false,
                    parentCode: null,
                    visibility: true
                });
            }
        });
        SideNavigationConstants.TEMP_ACTION_ACCESS.forEach((action) => {
            if (Utils.isInList(action.code.toUpperCase(), SideNavigationConstants.QA_ACTION_ACCESS)) {
                filteredActions.push({
                    code: action.code,
                    title: action.title,
                    description: action.description,
                    internalOnly: false,
                    specificRoleOnly: false,
                    parentCode: null,
                    visibility: true
                });
            }
        });
        // Only for SubConfigurations
        ConfigNavigationConstants.QA_CONFIG_ACTION_ACCESS.forEach((action) => {
            if (Utils.isInList(action.code.toUpperCase(), SideNavigationConstants.QA_ACTION_ACCESS)) {
                filteredActions.push({
                    code: action.code,
                    title: action.title,
                    description: action.description,
                    internalOnly: false,
                    specificRoleOnly: false,
                    parentCode: action.parentCode,
                    visibility: true
                });
                filteredActions = filteredActions.map((d) => {
                    if (Utils.equalsIgnoreCase(d.code, action.parentCode)) {
                        d.visibility = false;
                    }
                    return d;
                });
            }
        });

        const filteredUniqueActions = Utils.uniqueList(filteredActions, 'code');
        return of(plainToClass(ActionAccessResultDto, filteredUniqueActions));
    }

    updateSideNavigatorState(state: boolean): void {
        this._subjectSideNavigatorState.next(state);
    }

    getSideNavigatorState(): Observable<boolean> {
        return this._subjectSideNavigatorState;
    }

    updateTopMenuState(state: boolean): void {
        this._subjectTopMenuState.next(state);
    }

    getTopMenuState(): Observable<boolean> {
        return this._subjectTopMenuState;
    }

    getUserPreferences(): Observable<boolean> {
        return this._subjectChangesInPrefences;
    }

    updateUserPreferences(state: boolean): void {
        this._subjectChangesInPrefences.next(state);
    }

    hasUpdateInThemeColor(): Observable<boolean> {
        return this._subjectChangeInThemeColor;
    }

    updateInThemeColor(changeInThemeColor: boolean): void {
        this._subjectChangeInThemeColor.next(changeInThemeColor);
    }

    getBrandingInforamtion(): BrandingConfigurationResultDto {
        return this._brandingInforamtion;
    }

    setBrandingInforamtion(info: BrandingConfigurationResultDto): void {
        this._brandingInforamtion = info;
    }

    loadUserSettings(): Observable<boolean> {
        return new Observable((observer) => {
            if (this.authService.isLoggedIn) {
                this.setupLocalStorageService();
                this.localStorageService.loadItems().subscribe(() => {
                    observer.next(true);
                    observer.complete();
                });
            } else {
                observer.next(false);
                observer.complete();
            }
        });
    }

    getUserStorageKey(currentUserId: string, key: string, category?: CCLocalStorageCategory): string {
        const userId = currentUserId.replace(/ /gi, '_');
        const tokens = [`MP`, userId, category, key];
        return `${tokens.join('_').toUpperCase()}`;
    }

    getCloudDriveUrl(): Observable<CloudDriveUrlResultDto> {
        return this.httpClient
            .get(`${URLConstants.ccUserBaseApiUrl}/clouddriveurl`)
            .pipe(map((plain: any) => plainToClass(CloudDriveUrlResultDto, plain)));
    }

    private setupLocalStorageService(): void {
        if (this.authService.isLoggedIn) {
            this.localStorageService.portalType = Constants.portalType;
            this.localStorageService.userId = this.getCurrentUserId();
            this.localStorageService.getItemApi = this.getUserSettings.bind(this);
            this.localStorageService.setItemApi = this.saveUserSetting.bind(this);
            this.localStorageService.removeItemApi = this.deleteUserSetting.bind(this);
        }
    }

    private getUserSettings(param: UserSettingParmetersDto): Observable<UserSettingRawDto[]> {
        const url = URLConstants.ccUserSettingsApiUrl + param.getQueryString();
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(UserSettingRawDto, response)));
    }

    private saveUserSetting(param: UserSettingRawDto): Observable<ResultStatusDto> {
        return this.httpClient
            .post(URLConstants.ccUserSettingsApiUrl, classToPlain(param))
            .pipe(map((responseData) => plainToClass(ResultStatusDto, responseData)));
    }

    private deleteUserSetting(param: UserSettingParmetersDto): Observable<ResultStatusDto> {
        return this.httpClient
            .delete(URLConstants.ccUserSettingsApiUrl + param.getQueryString())
            .pipe(map((responseData) => plainToClass(ResultStatusDto, responseData)));
    }
}
